import type { ScopeEditorPersistenceState } from '../../types/scopeEditorPersistence.types';
import { createScopeEditorPersistenceState } from '../../types/scopeEditorPersistence.types';
import {
  DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES,
  DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES,
} from '../../config/mappingScopeEditorFieldDefaults';

export const PROCESS_CONDITIONS_EDITOR_STORAGE_KEY = 'pipeex-mapping-process-conditions-editor-v1';
export const PROJECT_PROPERTIES_EDITOR_STORAGE_KEY = 'pipeex-mapping-project-properties-editor-v1';

const SCOPE_EDITOR_PROFILE_STATE_EVENT = 'pipeex:mapping-scope-editor-profile-state';

const debugMappingScopeStorage = (_message: string, _payload?: unknown) => {};

export interface MappingScopeEditorProfileState {
  processConditions?: ScopeEditorPersistenceState | null;
  projectProperties?: ScopeEditorPersistenceState | null;
}

export interface ResolvedMappingScopeEditorProfileState {
  processConditions: ScopeEditorPersistenceState;
  projectProperties: ScopeEditorPersistenceState;
}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isStringRecord = (value: unknown): value is Record<string, string> =>
  typeof value === 'object' &&
  value !== null &&
  Object.values(value).every((entry) => typeof entry === 'string');

const isCustomRows = (
  value: unknown
): value is ScopeEditorPersistenceState['customRows'] =>
  Array.isArray(value) &&
  value.every(
    (entry) =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as { id?: unknown }).id === 'string' &&
      typeof (entry as { label?: unknown }).label === 'string' &&
      typeof (entry as { value?: unknown }).value === 'string'
  );

const normalizeMappingScopeEditorState = (
  rawState: unknown,
  defaultPcfAttributeNames: Record<string, string>
): ScopeEditorPersistenceState => {
  if (!rawState || typeof rawState !== 'object') {
    return createScopeEditorPersistenceState(defaultPcfAttributeNames);
  }

  const parsed = rawState as {
    deletedRowIds?: unknown;
    customRows?: unknown;
    pcfAttributeNames?: unknown;
  };

  return {
    deletedRowIds: isStringArray(parsed.deletedRowIds) ? parsed.deletedRowIds : [],
    customRows: isCustomRows(parsed.customRows) ? parsed.customRows : [],
    pcfAttributeNames: {
      ...defaultPcfAttributeNames,
      ...(isStringRecord(parsed.pcfAttributeNames) ? parsed.pcfAttributeNames : {}),
    },
  };
};

export const resolveMappingScopeEditorProfileState = (
  rawState: MappingScopeEditorProfileState | null | undefined
): ResolvedMappingScopeEditorProfileState => ({
  processConditions: normalizeMappingScopeEditorState(
    rawState?.processConditions,
    DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES
  ),
  projectProperties: normalizeMappingScopeEditorState(
    rawState?.projectProperties,
    DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES
  ),
});

export const loadMappingScopeEditorState = (
  storageKey: string,
  defaultPcfAttributeNames: Record<string, string>
): ScopeEditorPersistenceState => {
  if (typeof window === 'undefined') {
    return createScopeEditorPersistenceState(defaultPcfAttributeNames);
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      debugMappingScopeStorage('[MappingScopeEditorState] No saved state found', { storageKey });
      return createScopeEditorPersistenceState(defaultPcfAttributeNames);
    }

    const parsed = JSON.parse(raw) as {
      deletedRowIds?: unknown;
      customRows?: unknown;
      pcfAttributeNames?: unknown;
    };
    const state = normalizeMappingScopeEditorState(parsed, defaultPcfAttributeNames);

    debugMappingScopeStorage('[MappingScopeEditorState] Loaded state', {
      storageKey,
      deletedRowCount: state.deletedRowIds.length,
      customRowCount: state.customRows.length,
      renamedKeyCount: Object.keys(state.pcfAttributeNames).length,
    });

    return state;
  } catch {
    console.warn('[MappingScopeEditorState] Failed to parse saved state, resetting', {
      storageKey,
    });
    return createScopeEditorPersistenceState(defaultPcfAttributeNames);
  }
};

export const loadCurrentMappingScopeEditorProfileState = (): ResolvedMappingScopeEditorProfileState =>
  resolveMappingScopeEditorProfileState({
    processConditions: loadMappingScopeEditorState(
      PROCESS_CONDITIONS_EDITOR_STORAGE_KEY,
      DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES
    ),
    projectProperties: loadMappingScopeEditorState(
      PROJECT_PROPERTIES_EDITOR_STORAGE_KEY,
      DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES
    ),
  });

export const saveMappingScopeEditorState = (
  storageKey: string,
  state: ScopeEditorPersistenceState
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
    debugMappingScopeStorage('[MappingScopeEditorState] Saved state', {
      storageKey,
      deletedRowCount: state.deletedRowIds.length,
      customRowCount: state.customRows.length,
      renamedKeyCount: Object.keys(state.pcfAttributeNames).length,
    });
  } catch {
    console.warn('[MappingScopeEditorState] Failed to save state', { storageKey });
  }
};

export const applyMappingScopeEditorProfileState = (
  rawState: MappingScopeEditorProfileState | null | undefined
): ResolvedMappingScopeEditorProfileState => {
  const resolvedState = resolveMappingScopeEditorProfileState(rawState);

  saveMappingScopeEditorState(
    PROCESS_CONDITIONS_EDITOR_STORAGE_KEY,
    resolvedState.processConditions
  );
  saveMappingScopeEditorState(
    PROJECT_PROPERTIES_EDITOR_STORAGE_KEY,
    resolvedState.projectProperties
  );

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(SCOPE_EDITOR_PROFILE_STATE_EVENT, {
        detail: resolvedState,
      })
    );
  }

  return resolvedState;
};

export const subscribeToMappingScopeEditorProfileState = (
  listener: (state: ResolvedMappingScopeEditorProfileState) => void
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleEvent = (event: Event) => {
    const customEvent = event as CustomEvent<MappingScopeEditorProfileState | null | undefined>;
    listener(resolveMappingScopeEditorProfileState(customEvent.detail));
  };

  window.addEventListener(SCOPE_EDITOR_PROFILE_STATE_EVENT, handleEvent as EventListener);
  return () => {
    window.removeEventListener(SCOPE_EDITOR_PROFILE_STATE_EVENT, handleEvent as EventListener);
  };
};
